import type { RegionCode } from 'google-libphonenumber';
import { useEffect, useState } from 'react';
import { DEFAULT_ISO_CODE } from '../../../util/consts';
import { getCountriesForComboBox } from '../../../util/country';
import {
  getCountryPrefix,
  getFormattedExampleNumber,
  isValidNum,
  ensurePrefix,
} from '../../../util/phone';
import { MAX_LENGTH } from '../consts';

export interface UseLiliInternationalPhoneInputLogicParams {
  initialIsoCode?: RegionCode | null;
  inputValue: string;
  onIsoCodeChange?: (isoCode: RegionCode) => void;
  onInputValueChange: (val: string) => void;
  onFieldValidationStatusChange?: (isValid: boolean) => void;
  filteredCountries?: RegionCode[];
}

export const useLiliInternationalPhoneInputLogic = ({
  initialIsoCode,
  inputValue,
  onIsoCodeChange,
  onInputValueChange,
  onFieldValidationStatusChange,
  filteredCountries,
}: UseLiliInternationalPhoneInputLogicParams) => {
  const [selectedIsoCode, setSelectedIsoCode] = useState<RegionCode>(
    initialIsoCode || DEFAULT_ISO_CODE
  );

  const countries = getCountriesForComboBox(filteredCountries);
  const placeholder = getFormattedExampleNumber(selectedIsoCode ?? DEFAULT_ISO_CODE);

  const handleInputValueChange = (val: string) => {
    const prefix = getCountryPrefix(selectedIsoCode);
    if (val.length >= MAX_LENGTH + prefix.length) return;
    const formattedVal = ensurePrefix(val, prefix);

    onInputValueChange(formattedVal);
    onFieldValidationStatusChange?.(isValidNum(formattedVal));
  };

  const localOnIsoCodeChange = (isoCode: RegionCode) => {
    setSelectedIsoCode(isoCode);
    onIsoCodeChange?.(isoCode);

    onInputValueChange('');
  };

  const handleFocus = () => {
    if (!inputValue) onInputValueChange(`${getCountryPrefix(selectedIsoCode)} `);
  };

  useEffect(() => {
    onFieldValidationStatusChange?.(false);
  }, [selectedIsoCode]);

  useEffect(() => {
    if (initialIsoCode) localOnIsoCodeChange(initialIsoCode);
  }, [initialIsoCode]);

  useEffect(() => {
    onIsoCodeChange?.(initialIsoCode ?? DEFAULT_ISO_CODE);
    if (inputValue) {
      handleInputValueChange(inputValue);
    }
  }, []);

  return {
    selectedIsoCode,
    setSelectedIsoCode,
    countries,
    placeholder,
    localOnIsoCodeChange,
    handleInputValueChange,
    handleFocus,
  };
};
